import type { LearningStoreGetters, SubmitStatus } from "@/store/learning/getters";
import type { LearningStoreState } from "@/store/learning/state";
import LearningDirectoryType
    from "~/components/Learning/LearningMiddle/LearningMultiFileCode/LearningDirectory/config/LearningDirectoryType";
import { CodeOrderModel } from "~~/package/mapper/learning/model/CodeOrderModel";
import { LangType } from "~~/package/mapper/learning/model/LangType";
import { TaskType } from "~~/package/mapper/learning/model/TaskType";
import { useLearningAssessmentStore } from "@/store/learning/assessment/learningAssessmentStore";

export const codeGetters = {
    convertSavingCode(state: LearningStoreState) {
        const isWidgetLearning = state.task.markOrderList.length > 0
            || state.task.markDictationList.length > 0
            || state.task.markSelectionList.length > 0;

        let code = isWidgetLearning ? state.task.defaultCode : state.currentCode;

        state.task.markQuestionList.forEach(question => {
            code = code.replace(new RegExp(question.textRegex), "");
        });
        state.task.markDictationList.forEach(codeInput => {
            const inputText = codeInput.value !== undefined ? codeInput.value.replace("$", "$$$") : "";
            code = code.replace(new RegExp(codeInput.textRegex, "i"), inputText);
        });
        state.task.markSelectionList.forEach(codeSelection => {
            let selectionText = "";
            if (codeSelection.selectionList[codeSelection.selectedIndex] !== undefined)
                selectionText = codeSelection.selectionList[codeSelection.selectedIndex];
            code = code.replace(new RegExp(codeSelection.textRegex, "i"), selectionText.replace("$", "$$$"));
        });
        state.task.markOrderList.forEach((order: CodeOrderModel, index: number) => {
            let text = "";
            if (![undefined, -1].includes(order.answerIndex)) {
                text = state.task.markOrderList[order.answerIndex].text || "";
            }
            code = code.replace(new RegExp(order.textRegex, "i"),
                text !== undefined ? text.replace("$", "$$$") : "");
        });
        return code;
    },
    convertedCurrentCode(state: LearningStoreState & LearningStoreGetters) {
        let code = state.convertSavingCode;
        state.task.hintList.forEach(codeHint => {
            code = code.replace(new RegExp(codeHint.textRegex), "");
        });

        return code;
    },
    isFrontendLearning(state: LearningStoreState & LearningStoreGetters): boolean {
        return state.task.langType === LangType.HTML_CSS_JAVASCRIPT
            && [TaskType.MULTI_FILE_CODE].includes(state.task.type as TaskType);
    },
    isCodeWithoutRun(state: LearningStoreState & LearningStoreGetters): boolean {
        return state.task.type === TaskType.CODE_WITHOUT_RUN;
    },
    isCodeRelatedType(state: LearningStoreState & LearningStoreGetters): boolean {
        return ([TaskType.CODE, TaskType.MULTI_FILE_CODE, TaskType.CODE_WITHOUT_RUN] as TaskType[]).includes(state.task.type as TaskType);
    },
    isEmbed(state: LearningStoreState & LearningStoreGetters): boolean {
        return state.task.type === TaskType.EMBED;
    },
    hasUnsubmittedChange(state: LearningStoreState & LearningStoreGetters): boolean {
        const hasCurrentChange = state.task.type === TaskType.EMBED
            ? state.isEmbedContentChanged
            : state.isChangedButNotSubmit;
        // 기록은 ProSkillAssure 칩·팝업용이다 — 대회·학습의 이동/언어 변경 팝업 조건은 develop 그대로 둔다.
        return hasCurrentChange
            || (state.isProSkillAssure && state.unsubmittedChangeByTaskId[state.task.id] === true);
    },
    submitStatus(state: LearningStoreState & LearningStoreGetters): SubmitStatus {
        const assessmentStore = useLearningAssessmentStore();
        const currentTask = assessmentStore.proSkillAssure.assessment.setList
            .flatMap(set => set.taskList)
            .find(task => task.id === state.task.id);

        if (!currentTask?.completeFlag) return 'notSubmitted';

        if (state.hasUnsubmittedChange) return 'resubmitRequired';

        const restoresSubmittedAnswer = ([
            TaskType.MULTIPLE_CHOICE,
            TaskType.INSTRUCTION_MULTIPLE_CHOICE,
            TaskType.SHORT_QA,
            TaskType.INSTRUCTION_SHORT_QA,
            TaskType.DESCRIPTIVE_QA,
            TaskType.INSTRUCTION_DESCRIPTIVE_QA,
            TaskType.ACTIVITY_CODE,
        ] as TaskType[]).includes(state.task.type as TaskType);
        if (restoresSubmittedAnswer) return 'submitted';

        return Object.hasOwn(state.unsubmittedChangeByTaskId, state.task.id)
            ? 'submitted'
            : 'resubmitRequired';
    },
    getUserLearningDirectoryType(_state: LearningStoreState & LearningStoreGetters): LearningDirectoryType {
        return useLearningAssessmentStore().getUserLearningDirectoryType;
    }
};
